import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { resource } from "@util/resource";
import { DWS } from "@interfaces/dws";
import { OrderDetailV4 } from "@interfaces/dws/order-detail-v4";
import { OrderLogV4 } from "@interfaces/dws/order-log-v4";
import { OrderStatus } from "@interfaces/dws/order-status";
import { Country } from "@interfaces/dws/country";
import { OrderShippingData } from "@interfaces/dws/order-shipping-data";
import { PaymentMethod } from "@interfaces/dws/payment-method";
import * as moment from 'moment';

@Injectable({
  providedIn: "root",
})
export class CrmOrdersService {
  constructor() {}

  /** Normalize date query params for CRM order list/export (YYYY-MM-DD). */
  private normalizeOrderFilterDates(filters: DWS.Order.RequestFilter): void {
    if (!filters) {
      return;
    }

    const fmtStart = (v: string | undefined) =>
      v ? moment(v).startOf("day").format("YYYY-MM-DD") : undefined;
    const fmtEnd = (v: string | undefined) =>
      v ? moment(v).endOf("day").format("YYYY-MM-DD") : undefined;

    if (filters.orderDateStart) {
      const v = fmtStart(filters.orderDateStart as string);
      v ? (filters.orderDateStart = v) : delete filters.orderDateStart;
    } else {
      delete filters.orderDateStart;
    }

    if (filters.orderDateEnd) {
      const v = fmtEnd(filters.orderDateEnd as string);
      v ? (filters.orderDateEnd = v) : delete filters.orderDateEnd;
    } else {
      delete filters.orderDateEnd;
    }

    if (filters.registrationStartDate) {
      const v = fmtStart(filters.registrationStartDate as string);
      v ? (filters.registrationStartDate = v) : delete filters.registrationStartDate;
    } else {
      delete filters.registrationStartDate;
    }

    if (filters.registrationEndDate) {
      const v = fmtEnd(filters.registrationEndDate as string);
      v ? (filters.registrationEndDate = v) : delete filters.registrationEndDate;
    } else {
      delete filters.registrationEndDate;
    }
  }

  private normalizeLegacyAndLastEditDates(filters: DWS.Order.RequestFilter): void {
    if (!filters) {
      return;
    }

    if (filters.startDate || filters.endDate) {
      const startDate = filters.startDate || "";
      const endDate = filters.endDate || "";
      startDate
        ? (filters.startDate = moment(startDate).startOf("day").format("YYYY-MM-DD"))
        : delete filters.startDate;
      endDate
        ? (filters.endDate = moment(endDate).endOf("day").format("YYYY-MM-DD"))
        : delete filters.endDate;
    }

    if (filters.lastEditStartDate || filters.lastEditEndDate) {
      const lastEditStartDate = filters.lastEditStartDate || "";
      const lastEditEndDate = filters.lastEditEndDate || "";
      lastEditStartDate
        ? (filters.lastEditStartDate = moment(lastEditStartDate)
            .startOf("day")
            .format("YYYY-MM-DD"))
        : delete filters.lastEditStartDate;
      lastEditEndDate
        ? (filters.lastEditEndDate = moment(lastEditEndDate)
            .endOf("day")
            .format("YYYY-MM-DD"))
        : delete filters.lastEditEndDate;
    }
  }

  /**
   * Spring binds List&lt;...&gt; from repeated query params (country=1&country=2).
   * The orders UI joins multiselects with commas — expand to string[] for URL building.
   */
  private expandListQueryParams(filters: DWS.Order.RequestFilter): void {
    const listKeys: (keyof DWS.Order.RequestFilter)[] = [
      "country",
      "status",
      "paymentType",
      "wineryChannelName",
    ];
    for (const key of listKeys) {
      const raw = filters[key] as unknown;
      if (raw === undefined || raw === null || raw === "") {
        delete (filters as any)[key];
        continue;
      }
      if (Array.isArray(raw)) {
        const parts = (raw as unknown[]).map((x) => String(x)).filter((s) => s.length > 0);
        if (parts.length === 0) {
          delete (filters as any)[key];
        } else {
          (filters as any)[key] = parts;
        }
        continue;
      }
      if (typeof raw === "string") {
        const parts = raw.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
        if (parts.length === 0) {
          delete (filters as any)[key];
        } else {
          (filters as any)[key] = parts;
        }
      }
    }
  }

  /** Drop empty / non-serializable values so the CRM URL builder does not emit bad query pairs. */
  private compactOrderQueryParams(
    filters: DWS.Order.RequestFilter
  ): Record<string, string | number | string[]> {
    const out: Record<string, string | number | string[]> = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v === undefined || v === null) {
        continue;
      }
      if (v === "") {
        continue;
      }
      if (typeof v === "object" && !Array.isArray(v)) {
        continue;
      }
      if (Array.isArray(v) && v.length === 0) {
        continue;
      }
      (out as any)[k] = v;
    }
    return out;
  }

  private prepareOrderQueryParams(filters: DWS.Order.RequestFilter): Record<string, string | number | string[]> {
    this.normalizeOrderFilterDates(filters);
    this.normalizeLegacyAndLastEditDates(filters);
    this.expandListQueryParams(filters);
    return this.compactOrderQueryParams(filters);
  }

  list(filters: DWS.Order.RequestFilter, page: number = 0, count: number = 999999): Observable<DWS.Page<DWS.CrmOrder>> {
    const prepared = this.prepareOrderQueryParams({ ...filters });
    return resource("crm://orders")
      .params({ page, count, ...prepared })
      .get<DWS.Page<DWS.CrmOrder>>();
  }

  listByCodes(codes: string[]): Observable<DWS.CrmOrder[]> {
    return resource("crm://orders/by-codes").params({ codes }).get<DWS.CrmOrder[]>();
  }

  get(orderId: string): Observable<DWS.CrmOrder> {
    return resource("crm://orders/id").params({ id: orderId }).get<DWS.CrmOrder>();
  }

  /** WS-2158: CRM order header (orders, order_totals) + lines (order_products, order_product_details) + metadata. */
  getOrderDetailV4(orderId: string): Observable<OrderDetailV4.Order | null> {
    return resource("crm://orders/v4/id/order-details").params({ id: orderId }).get<OrderDetailV4.Order | null>();
  }

  /** Updates only CRM notes (orders.notes); does not change status or full payload. */
  updateOrderNotesV4(orderId: string, notes: string): Observable<void> {
    return resource("crm://orders/v4/id/notes").params({ id: orderId }).put<void>({ notes });
  }

  /** Change history from {@code order_logs} (newest first). */
  getOrderLogsV4(orderId: string): Observable<OrderLogV4[]> {
    return resource("crm://orders/v4/id/logs").params({ id: orderId }).get<OrderLogV4[]>();
  }

  getByCode(orderCode: string): Observable<DWS.CrmOrder> {
    return resource("crm://orders/by-code").params({ code: orderCode }).get<DWS.CrmOrder>();
  }

  create(order: DWS.Order.New): Observable<DWS.CrmOrder> {
    return resource("crm://orders").post<DWS.CrmOrder>(order);
  }

  update(orderId: any, order: DWS.Order.New): Observable<DWS.CrmOrder> {
    return resource("crm://orders/id").params({ id: orderId }).put<DWS.CrmOrder>(order);
  }

  /** Bonifico ricevuto: PATCH /orders/{id}/status/{status} (es. pending-payment -> in-progress). */
  updateOrderStatus(orderId: string, status: string): Observable<DWS.CrmOrder> {
    return resource("crm://orders/id/status-value").params({ id: orderId, status }).patch<DWS.CrmOrder>({});
  }

  sendReceiptEmail(orderId: string, email?: string, language?: string): Observable<void> {
    const payload: any = {};
    if (email) {
      payload.email = email;
    }
    if (language) {
      payload.language = language;
    }
    return resource("crm://orders/id/send-receipt-email").params({ id: orderId }).post<void>(payload);
  }

  /**
   * Sends registered {@code order-receipt-to-guest} copies to the given addresses only (CRM manual forward).
   * Does not run POS fiscal receipt creation.
   */
  sendOrderReceiptCopies(orderId: string, recipients: string[], language?: string): Observable<void> {
    const payload: { recipients: string[]; language?: string } = { recipients };
    if (language) {
      payload.language = language;
    }
    return resource("crm://orders/id/send-receipt-email").params({ id: orderId }).post<void>(payload);
  }

  /**
   * Sends receipt email for v2 flow
   * This endpoint creates receipt with Fiskaly integration before sending email
   */
  sendReceiptEmailV2(orderId: string, email?: string, language?: string): Observable<void> {
    const payload: any = {};
    if (email) {
      payload.email = email;
    }
    if (language) {
      payload.language = language;
    }
    return resource("crm://orders/id/send-receipt-email").params({ id: orderId }).post<void>(payload);
  }

  delete(orderId: string) {
    return resource("crm://orders/id").params({ id: orderId }).delete();
  }

  orderExport(filters: CrmOrderFiltersExport, page: number = 0, count: number = 999999): Observable<DWS.Page<DWS.CrmOrder>> {
    const f = { ...filters } as DWS.Order.RequestFilter;

    if (f.dateRangeUpdate && f.dateRangeUpdate.start) {
      f.lastEditStartDate = moment(f.dateRangeUpdate.start).startOf("day").format("YYYY-MM-DD");
      if (f.dateRangeUpdate.end) {
        f.lastEditEndDate = moment(f.dateRangeUpdate.end).endOf("day").format("YYYY-MM-DD");
      } else {
        delete f.lastEditEndDate;
      }
    }
    delete (f as CrmOrderFiltersExport).dateRangeUpdate;
    delete (f as CrmOrderFiltersExport).dateRangeCreation;
    delete (f as CrmOrderFiltersExport).dateRangeOrderDate;

    const prepared = this.prepareOrderQueryParams(f);
    return resource("crm://orders/export")
      .params({ ...prepared, page, count })
      .get<DWS.Page<DWS.CrmOrder>>();
  }

  getOrdersCountries(wineryId: string): Observable<Country[]> {
    return resource("crm://orders/orders-countries").params({ wineryId }).get<Country[]>();
  }

  getOrdersStatuses(wineryId: string): Observable<OrderStatus[]> {
    return resource("crm://orders/orders-statuses").params({ wineryId }).get<OrderStatus[]>();
  }

  getOrderShippingData(orderId: number): Observable<OrderShippingData> {
    return resource("crm://orders/order-shipping-data").params({ orderId }).get<OrderShippingData>();
  }

  getOrdersPaymentTypes(wineryId: string): Observable<PaymentMethod[]> {
    return resource("crm://orders/orders-payment-types").params({ wineryId }).get<Country[]>();
  }
}

export interface CrmOrderFilters {
  wineryId: string;
  contactId?: number;
  wineryChannelId?: string;
}

export interface CrmOrderFiltersExport {
  wineryId: string;
  startDate?: string | Date;
  endDate?: string | Date;
  orderDateStart?: string;
  orderDateEnd?: string;
  registrationStartDate?: string;
  registrationEndDate?: string;
  wineryChannelId?: string;
  code?: string;
  id?: string;
  /** Comma-separated order ids (Da seguire export). */
  ids?: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
  country?: Country;
  quantity?: string;
  amount?: any;
  wineryChannelName?: string;
  paymentType?: PaymentMethod;
  status?: OrderStatus;
  lastEditStartDate?: string;
  lastEditEndDate?: string;

  dateRangeCreation?: {
    start?: string;
    end?: string;
  };

  dateRangeUpdate?: {
    start?: string;
    end?: string;
  };

  dateRangeOrderDate?: {
    start?: string;
    end?: string;
  };
}
